import { useCallback, useState } from "react";

import axiosInstance from "@/api/config";
import type { LlmErrorReason } from "@/constants/errorCode";
import { CustomError } from "@/utils/errors";

import { EventSourceMessage } from "@microsoft/fetch-event-source";

import useEventSource from "../useEventSource";

export type UseExecutiveSummaryProps = {
  analysisId?: string;
  onDelta: (chunk: { content: string }, event: EventSourceMessage) => void;
  onFinal: (full: { content: string }, event: EventSourceMessage) => void;
  onDone?: (done: ExecutiveSummaryDone, event: EventSourceMessage) => void;
  onError?: (error: ExecutiveSummaryError) => void;
};

export type ExecutiveSummaryFeedback = {
  comment?: string | null;
  rating: "positive" | "negative";
  reason?: string | null;
};

export type ExecutiveSummaryDone = {
  usage?: {
    input_tokens?: number;
    output_tokens?: number;
    total_tokens?: number;
  };
  feedback?: ExecutiveSummaryFeedback | null;
};

/**
 * Error payload of the SSE `stream:error` event, e.g.
 * `{ code: 429, message: "...", reason: "ERR_LLM_QUOTA_EXCEEDED" }`.
 * Transport failures are normalised to this same shape.
 */
export type ExecutiveSummaryError = {
  code?: number;
  message?: string;
  reason?: LlmErrorReason;
};

export type ExecutiveSummaryHandlers = {
  "message:delta": { content: string };
  "message:final": { content: string };
  "message:done": ExecutiveSummaryDone;
  "stream:error": ExecutiveSummaryError;
};

export type UseExecutiveSummaryResult = {
  /** Reopens the stream from scratch. */
  retry: () => void;
};

const toExecutiveSummaryError = (
  error: CustomError<unknown, LlmErrorReason>
): ExecutiveSummaryError => ({
  code: error.code,
  message: error.message,
  reason: error.reason
});

export const useExecutiveSummary = ({
  analysisId,
  onDelta,
  onFinal,
  onDone,
  onError
}: UseExecutiveSummaryProps): UseExecutiveSummaryResult => {
  // Bumped by retry() so the eventKey changes and useEventSource reconnects.
  const [retryToken, setRetryToken] = useState(0);

  useEventSource<ExecutiveSummaryHandlers, LlmErrorReason>({
    url: `${axiosInstance.defaults.baseURL}/v1/analysis/${analysisId}/executive-summary`,
    eventKey: ["executive-summary", analysisId, retryToken],
    parse: JSON.parse,
    eventHandlers: {
      "message:delta": (data, event) => {
        onDelta(data, event);
      },
      "message:final": (data, event) => {
        onFinal(data, event);
      },
      "message:done": (data, event) => {
        onDone?.(data, event);
      },
      "stream:error": (data) => {
        onError?.(data);
      }
    },
    // The backend reports failures as a terminal `stream:error` event, so
    // reconnecting would only replay the same error.
    closeOn: ["stream:error"],
    onRetriesExhausted: (error) => {
      onError?.(toExecutiveSummaryError(error));
    },
    enabled: !!analysisId
  });

  const retry = useCallback(() => {
    setRetryToken((prev) => prev + 1);
  }, []);

  return { retry };
};
